import { prop } from 'remeda';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { ROLES, type UserRole } from '@@auth/constants';
import checkAuthentication from '@@auth/utils/checkAuthentication';
import parseJWT from '@@auth/utils/parseJWT';

const MS = 1000;

const AUTH_STORE = 'authStore';

export type AuthState = {
    tokenType: string | null;
    accessToken: string | null;
    jti: string | null;
    userId: string | null;
    userRole: UserRole | null;
    expiresIn: number;
    loggedInTime: number;
};

type AuthActions = {
    expire: () => void;
    hydrateAuth: (payload: {
        access_token: string;
        token_type: string;
        expires_in: number | string;
    }) => void;
    clearAuth: () => void;
};

const initialState: AuthState = {
    tokenType: null,
    accessToken: null,
    jti: null,
    userId: null,
    userRole: null,
    expiresIn: 0,
    loggedInTime: 0,
};

export const chooseRole = (role?: UserRole): UserRole => {
    if (role && Object.values(ROLES).includes(role)) {
        return role;
    }

    if (role) {
        console.warn('invalid user role supplied', role);
    }

    return ROLES.EDITOR;
};

export const deriveUserStateFromToken = (
    token: string,
): {
    userId: string | null;
    userRole: UserRole;
} => {
    const tokenData = parseJWT(token);

    if (!tokenData) {
        return {
            userId: null,
            userRole: ROLES.EDITOR,
        };
    }

    return {
        userId: tokenData.unity_user_id ?? null,
        userRole: chooseRole(tokenData.authorities && (prop(tokenData.authorities, 0) as UserRole)),
    };
};

export const useAuthStore = create<AuthState & AuthActions>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,

                expire: () =>
                    set(
                        {
                            ...initialState,
                            expiresIn: 0,
                        },
                        false,
                        'expire',
                    ),

                // TODO:
                // next line comes from location after the redirect, it also contains jti
                // it's kinda weird as everything is passes using the url,.. the token alone
                // carries all information.
                hydrateAuth: ({ access_token, token_type, expires_in }) => {
                    const { userId, userRole } = deriveUserStateFromToken(access_token);

                    set(
                        {
                            ...initialState,
                            tokenType: token_type,
                            accessToken: access_token,
                            // Work with milliseconds everywhere!!!!
                            expiresIn: Number(expires_in) * MS,
                            loggedInTime: Date.now(),
                            userId,
                            userRole: chooseRole(userRole),
                        },
                        false,
                        'hydrateAuth',
                    );
                },

                clearAuth: () => set({ ...initialState }, false, 'clearAuth'),
            }),
            {
                name: AUTH_STORE,
                version: 1,
            },
        ),
        { name: AUTH_STORE, enabled: true },
    ),
);

export const selectAuthToken = (state: AuthState) =>
    state.tokenType && state.accessToken ? `${state.tokenType} ${state.accessToken}` : '';

export const selectIsAuthenticated = (state: AuthState) =>
    checkAuthentication(state.loggedInTime, state.expiresIn);

export const getIsAuthenticated = () => selectIsAuthenticated(useAuthStore.getState());

export const useIsAuthenticated = () => useAuthStore(selectIsAuthenticated);
